// Full-screen success animation after verification
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Fade,
  Zoom,
  Grow,
  useTheme,
  alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import RefreshIcon from '@mui/icons-material/Refresh';
import BlockIcon from '@mui/icons-material/Block';
import EmailIcon from '@mui/icons-material/Email';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

export default function VerificationSuccessScreen({ subscriptions = [], onClose }) {
  const theme = useTheme();
  const [showContent, setShowContent] = useState(false);
  const [showSubscriptions, setShowSubscriptions] = useState(false);

  useEffect(() => {
    console.log('🎊 VerificationSuccessScreen mounted!');
    console.log(`📊 Subscriptions count: ${subscriptions.length}`);
    
    // Stagger animations
    const timer1 = setTimeout(() => setShowContent(true), 500);
    const timer2 = setTimeout(() => setShowSubscriptions(true), 1500);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [subscriptions]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: alpha(theme.palette.background.default, 0.98),
        backdropFilter: 'blur(10px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        overflowY: 'auto',
      }}
    >
      <Container maxWidth="md" sx={{ my: 'auto' }}>
        <Fade in timeout={500}>
          <Paper
            elevation={8}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 4,
              textAlign: 'center',
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            {/* Success Icon */}
            <Zoom in timeout={700}>
              <Box
                sx={{
                  width: { xs: 100, sm: 120 },
                  height: { xs: 100, sm: 120 },
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 2rem',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.5)}`,
                  animation: 'successPulse 1s ease-in-out',
                  '@keyframes successPulse': {
                    '0%': {
                      transform: 'scale(0)',
                      opacity: 0,
                    },
                    '50%': {
                      transform: 'scale(1.1)',
                    },
                    '100%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                  },
                }}
              >
                <CheckCircleIcon
                  sx={{
                    fontSize: { xs: 48, sm: 64 },
                    color: 'white',
                  }}
                />
              </Box>
            </Zoom>

            {/* Title */}
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: theme.palette.success.main,
                mb: 1,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
              }}
            >
              🎉 You're Verified!
            </Typography>

            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 3, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}
            >
              Your OnlyFans account has been successfully verified
            </Typography>

            {/* Important Information */}
            <Grow in={showContent} timeout={1000}>
              <Box>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    mb: 3,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: theme.palette.info.main,
                      fontWeight: 600,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                    }}
                  >
                    <SecurityIcon /> Important Information
                  </Typography>

                  <List dense sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Login Notification"
                        secondary="You may receive an email from OnlyFans - that was us verifying your account."
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <VerifiedUserIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Subscription Data Only"
                        secondary="We immediately closed your session after fetching your subscriptions. We only access subscription data - never messages, content, or other information."
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <RefreshIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Future Updates"
                        secondary="To refresh your subscriptions later, we'll ask for your credentials again."
                      />
                    </ListItem>
                  </List>
                </Paper>

                {/* Subscriptions List */}
                {subscriptions && subscriptions.length > 0 && (
                  <Fade in={showSubscriptions} timeout={1000}>
                    <Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        }}
                      >
                        ✅ Found {subscriptions.length} Subscription{subscriptions.length !== 1 ? 's' : ''}
                      </Typography>

                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          mb: 3,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          maxHeight: { xs: 150, sm: 200 },
                          overflowY: 'auto',
                        }}
                      >
                        <List dense>
                          {subscriptions.slice(0, 10).map((sub, index) => {
                            // Handle both data structures:
                            // 1. Enriched structure from dashboard: { creator: {...}, reviewed: bool }
                            // 2. Simple structure: { username, name, ... }
                            const creator = sub.creator || sub;
                            const username = creator.username || 'unknown';
                            const name = creator.name || creator.username || 'Unknown Creator';
                            const avatar = creator.profilePicture || '';
                            
                            return (
                              <ListItem key={index}>
                                <ListItemAvatar>
                                  <Avatar
                                    src={avatar}
                                    alt={username}
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                    }}
                                  >
                                    {!avatar && (name?.[0] || '?')}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={name}
                                  secondary={`@${username}`}
                                />
                              </ListItem>
                            );
                          })}
                          {subscriptions.length > 10 && (
                            <ListItem>
                              <ListItemText
                                secondary={`...and ${subscriptions.length - 10} more`}
                                sx={{ fontStyle: 'italic' }}
                              />
                            </ListItem>
                          )}
                        </List>
                      </Paper>
                    </Box>
                  </Fade>
                )}

                {/* Action Button */}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={onClose}
                  sx={{
                    px: { xs: 4, sm: 6 },
                    py: { xs: 1.25, sm: 1.5 },
                    borderRadius: 3,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, 0.5)}`,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Start Reviewing Creators
                </Button>
              </Box>
            </Grow>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
}

